import { useCallback, useEffect, useRef, useState } from 'react';
import { APIGetAvailabilityCoverage } from '../api';

// Счётчики не участвуют в загрузке личного графика: обновление не трогает черновик.
function validateCoverage(data, weekStart) {
    if (data?.week_start !== weekStart || data.counts_include_self !== false
        || !['photographers', 'administrators', null].includes(data.viewer_group)
        || !Array.isArray(data.days) || data.days.length !== 7) {
        throw new Error('Не удалось прочитать количество доступных сотрудников.');
    }
    const byDate = {};
    for (let i = 0; i < 7; i += 1) {
        const day = new Date(`${weekStart}T00:00:00Z`);
        day.setUTCDate(day.getUTCDate() + i);
        const date = day.toISOString().slice(0, 10);
        const row = data.days.find(item => item.date === date);
        if (!row || !['photographers', 'administrators'].every(group =>
            Number.isSafeInteger(row[group]) && row[group] >= 0)) {
            throw new Error('Не удалось прочитать количество доступных сотрудников.');
        }
        byDate[date] = row;
    }
    return { ...data, byDate };
}

export default function useAvailabilityCoverage({ token, weekStart, enabled }) {
    const key = `${token || ''}:${weekStart}`;
    const [state, setState] = useState({ key: null, data: null, loading: false, error: '' });
    const refreshRef = useRef(null);
    const refresh = useCallback(() => refreshRef.current?.(), []);

    useEffect(() => {
        if (!enabled || !token || !weekStart) return undefined;
        let live = true;
        let inFlight = false;
        let controller;
        const load = async () => {
            if (!live || inFlight || document.hidden) return;
            inFlight = true;
            controller = new AbortController();
            setState(previous => ({ key, data: previous.key === key ? previous.data : null, loading: true, error: previous.key === key ? previous.error : '' }));
            try {
                const response = await APIGetAvailabilityCoverage(token, weekStart, controller.signal);
                const data = validateCoverage(response, weekStart);
                if (live) setState({ key, data, loading: false, error: '' });
            } catch (error) {
                if (live && !controller.signal.aborted) {
                    setState(previous => ({ key, data: previous.key === key ? previous.data : null, loading: false,
                        error: error.status === 401 ? 'Сессия истекла. Войди заново.'
                            : error.status === 403 ? 'Сервер отказал в доступе к количеству фотографов. Проверь роль аккаунта.'
                            : error.status === 404 ? 'Обнови серверную часть: сводка фотографов пока недоступна.'
                            : error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED'
                                ? 'Нет связи с сервером. Повтори обновление.'
                                : error.message || 'Не удалось обновить количество фотографов.' }));
                }
            } finally {
                inFlight = false;
            }
        };
        refreshRef.current = load;
        load();
        const timer = setInterval(load, 30000);
        const onVisible = () => { if (!document.hidden) load(); };
        window.addEventListener('focus', onVisible);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            live = false;
            controller?.abort();
            clearInterval(timer);
            window.removeEventListener('focus', onVisible);
            document.removeEventListener('visibilitychange', onVisible);
            if (refreshRef.current === load) refreshRef.current = null;
        };
    }, [enabled, key, token, weekStart]);

    const current = enabled && state.key === key;
    return { data: current ? state.data : null, error: current ? state.error : '',
        loading: !!enabled && (!current || state.loading), refresh };
}
